import { useRouter } from "@/lib/nav";
import { ReactNode, useMemo, useState, useTransition } from "react";
import { toast } from "sonner";

import EmptyState from "@buildpy/ui/empty-state";
import InfoTip from "@buildpy/ui/info-tip";
import { Input } from "@buildpy/ui/input";
import ListTable from "@buildpy/ui/list-table";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { TableCell, TableRow } from "@buildpy/ui/table";
import { EMPTY_MARK, formatNumber, formatRate, parseMoney } from "@buildpy/core/lib/format";
import type { ProfitRow, QuoteProfit as QuoteProfitData } from "@buildpy/core/modules/quote/profit";
import { saveActualCosts } from "@/api/actions/quote";
import Money from "@/components/money";
import { ListCard } from "@/components/responsive-list";
import SaveBar from "@/components/save-bar";
import { settleRedirect } from "@/lib/redirect-error";
import { usePhone } from "@/modules/quote/ui/editor/mobile/use-phone";

interface Draft {
  amount: number | null;
  memo: string;
}

type Drafts = Record<string, Draft>;

export const NO_COST_TEXT = "원가를 넣으면 이익률이 보입니다";
const MEMO_ONLY_BLOCK = "메모는 실제 원가와 같이 저장됩니다 — 금액을 넣어 주세요.";

const savedDraft = (row: ProfitRow): Draft => ({ amount: row.actual, memo: row.memo ?? "" });
const sameDraft = (a: Draft, b: Draft): boolean => a.amount === b.amount && a.memo.trim() === b.memo.trim();
// 칸을 비우면 null(실제 원가 지우기) — 0 은 「0원 썼다」 라 남긴다.
const amountInput = (text: string): number | null => (text.replace(/[^\d]/g, "") === "" ? null : parseMoney(text));
const rateText = (bp: number | null): string => (bp === null ? EMPTY_MARK : formatRate(bp / 10000));
// 원가가 빠진 줄이 있는 공종 수 — 합계 이익률이 부푼 값이라 알린다(원가를 하나도 안 넣었으면 NO_COST_TEXT 가 대신한다).
export const costMissingNote = (profit: QuoteProfitData): string | null => {
  const n = profit.rows.filter((r) => r.cost_missing_count > 0).length;
  return profit.no_cost || n === 0 ? null : `원가 미입력 공종 ${n}개 — 이익률이 높게 보일 수 있습니다`;
};

// 실제가 견적 원가보다 많으면(+) 주의 톤, 적으면(−) 완료 톤.
const DiffPill = ({ diff }: { diff: number | null }) =>
  diff === null ? (
    <span className="text-xs text-muted-foreground">아직</span>
  ) : (
    <Pill tone={diff > 0 ? "hold" : diff < 0 ? "done" : "neutral"}>
      <Money signed value={diff} />
    </Pill>
  );

const HeadTip = ({ label, tip, sub }: { label: string; tip: string; sub?: string }) => (
  <span className="inline-flex flex-col items-end">
    <span className="inline-flex flex-wrap items-center gap-x-1">
      {label}
      <InfoTip label={`${label} 안내`}>{tip}</InfoTip>
    </span>
    {sub && <span className="text-xs font-normal text-muted-foreground">{sub}</span>}
  </span>
);

interface Props {
  quoteId: string;
  profit: QuoteProfitData | null;
  // 고칠 수 없는 까닭(지난 버전·권한 없음) — 있으면 입력을 잠그고 저장 막대를 숨긴다.
  lockedReason?: string | null;
}

// 견적 손익(내부) 탭 — 공종별 판매·견적 원가·이익률(부가세 빼고)과 실제 원가 입력 · 고객 화면엔 없다.
const QuoteProfit = ({ quoteId, profit, lockedReason = null }: Props) => {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [drafts, setDrafts] = useState<Drafts>({});
  const phone = usePhone();
  const rows = useMemo(() => profit?.rows ?? [], [profit]);

  const draftOf = (row: ProfitRow): Draft => drafts[row.trade] ?? savedDraft(row);
  const changed = useMemo(
    () => rows.filter((row) => drafts[row.trade] !== undefined && !sameDraft(drafts[row.trade], savedDraft(row))),
    [rows, drafts]
  );
  const blocked = changed.some((row) => drafts[row.trade].amount === null && drafts[row.trade].memo.trim() !== "")
    ? MEMO_ONLY_BLOCK
    : undefined;

  if (!profit) {
    return (
      <SectionCard description="부가세 빼고 · 고객에게 안 보입니다" padding="sm" title="공종별 손익" data-testid="quote-profit">
        <EmptyState variant="plain">공종을 넣으면 손익이 보입니다.</EmptyState>
      </SectionCard>
    );
  }

  const { totals } = profit;
  const missingNote = costMissingNote(profit);
  const live = rows.map((row) => {
    const draft = draftOf(row);
    return { row, draft, diff: draft.amount === null ? null : draft.amount - row.cost };
  });
  const withActual = live.filter((r) => r.diff !== null);
  const liveDiff = withActual.length === 0 ? null : withActual.reduce((sum, r) => sum + (r.diff ?? 0), 0);

  const patch = (row: ProfitRow, next: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [row.trade]: { ...(prev[row.trade] ?? savedDraft(row)), ...next } }));

  const onSave = () => {
    const payload = changed.map((row) => {
      const draft = drafts[row.trade];
      const memo = draft.memo.trim();
      return { trade: row.trade, amount: draft.amount, memo: memo === "" ? null : memo };
    });
    startTransition(async () => {
      try {
        const result = await saveActualCosts(quoteId, payload);
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        toast.success("실제 원가를 저장했습니다.");
        setDrafts({});
        router.refresh();
      } catch (caught) {
        if (settleRedirect(caught)) return;
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  const amountField = (row: ProfitRow, draft: Draft, size: "xs" | "default"): ReactNode => (
    <Input
      aria-label={`${row.name} 실제 원가`}
      className="text-right tabular-nums"
      disabled={pending || lockedReason !== null}
      inputMode="numeric"
      inputSize={size}
      name={`actual-${row.trade}`}
      placeholder="아직"
      value={draft.amount === null ? "" : formatNumber(draft.amount)}
      onChange={(event) => patch(row, { amount: amountInput(event.target.value) })}
    />
  );

  const memoField = (row: ProfitRow, draft: Draft, size: "xs" | "default"): ReactNode => (
    <Input
      aria-label={`${row.name} 메모`}
      disabled={pending || lockedReason !== null}
      inputSize={size}
      maxLength={200}
      name={`actual-memo-${row.trade}`}
      placeholder="메모"
      value={draft.memo}
      onChange={(event) => patch(row, { memo: event.target.value })}
    />
  );

  const marginCell = (row: ProfitRow): ReactNode => (
    <>
      {rateText(row.profit_rate_bp)}
      {row.cost_missing_count > 0 && <span className="block text-xs text-muted-foreground">원가 빠진 줄 {row.cost_missing_count}</span>}
    </>
  );

  return (
    <div className="flex min-w-0 flex-col gap-y-3" data-testid="quote-profit">
      <SectionCard
        actions={<span className="text-xs text-muted-foreground">부가세 빼고 · 고객에게 안 보입니다</span>}
        description="실제 원가는 시공 중·후에 입력 — 견적 원가와 차이를 본다"
        padding="sm"
        title="공종별 손익">
        {lockedReason && (
          <p className="mb-3 text-sm text-muted-foreground" data-testid="quote-profit-locked">
            {lockedReason}
          </p>
        )}
        {profit.no_cost && (
          <p className="mb-3 text-sm text-muted-foreground" data-testid="quote-profit-no-cost">
            {NO_COST_TEXT}
          </p>
        )}

        {phone !== true && (
          <ListTable
            className="hidden md:block"
            columns={[
              { label: "공종" },
              {
                label: <HeadTip label="판매" sub="부가세 빼고" tip="공종 금액에서 부가세를 뺀 값. 이익률을 공급가로 재야 해서 이 표는 전부 부가세 빼고 센다." />,
                align: "right",
              },
              { label: <HeadTip label="견적 원가" tip="견적을 쓸 때 넣은 원가의 공종별 합." />, align: "right" },
              { label: <HeadTip label="이익률" tip="이익 ÷ 공급가. 마진 +20% 는 이익률 16.7% — 기준이 달라 숫자가 작다." />, align: "right" },
              {
                label: <HeadTip label="실제 원가" tip="시공 중·후에 실제로 쓴 돈. 넣으면 견적 원가와의 차이가 나온다." />,
                align: "right",
                className: "w-32",
              },
              { label: "차이 · 메모", className: "w-44" },
            ]}
            footer={
              <TableRow>
                <TableCell className="px-3 py-2">
                  <b>합계</b>
                  <span className="block text-xs text-muted-foreground">
                    + 관리비 <Money value={totals.mgmt} /> · 절사 (부가세 빼고)
                  </span>
                </TableCell>
                <TableCell className="px-3 py-2 text-right font-semibold">
                  <Money value={totals.sale} />
                </TableCell>
                <TableCell className="px-3 py-2 text-right font-semibold">
                  <Money value={profit.no_cost ? null : totals.cost} />
                </TableCell>
                <TableCell className="px-3 py-2 text-right" data-testid="quote-profit-total-margin">
                  <b>{rateText(totals.profit_rate_bp)}</b>
                  {totals.supply_profit_rate_bp !== null && (
                    <span className="block whitespace-nowrap text-xs text-muted-foreground">
                      관리비 넣으면 {rateText(totals.supply_profit_rate_bp)}
                    </span>
                  )}
                  {missingNote && (
                    <span className="block text-xs text-warning" data-testid="quote-profit-cost-missing">
                      {missingNote}
                    </span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-right text-xs text-muted-foreground">
                  {withActual.length} / {totals.trade_count} 공종
                </TableCell>
                <TableCell className="px-3 py-2">
                  <Money fallback={EMPTY_MARK} signed value={liveDiff} />
                </TableCell>
              </TableRow>
            }>
            {live.map(({ row, draft, diff }) => (
              <TableRow key={row.trade} data-profit-row={row.trade}>
                <TableCell className="whitespace-nowrap px-3 py-2">{row.name}</TableCell>
                <TableCell className="px-3 py-2 text-right">
                  <Money value={row.sale} />
                </TableCell>
                <TableCell className="px-3 py-2 text-right">
                  <Money value={row.has_cost ? row.cost : null} />
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{marginCell(row)}</TableCell>
                <TableCell className="px-3 py-2">{amountField(row, draft, "xs")}</TableCell>
                <TableCell className="px-3 py-2">
                  <div className="flex flex-col items-start gap-y-1">
                    <DiffPill diff={diff} />
                    {memoField(row, draft, "xs")}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </ListTable>
        )}

        {phone !== false && (
          <ul aria-label="공종별 손익" className="flex min-w-0 flex-col gap-y-2 md:hidden">
            {live.map(({ row, draft, diff }) => (
              <ListCard key={row.trade}>
                <div className="flex items-center justify-between gap-x-2">
                  <span className="font-semibold">{row.name}</span>
                  <DiffPill diff={diff} />
                </div>
                <dl className="grid grid-cols-3 gap-x-2 text-xs">
                  <div>
                    <dt className="text-muted-foreground">판매</dt>
                    <dd>
                      <Money value={row.sale} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">견적 원가</dt>
                    <dd>
                      <Money value={row.has_cost ? row.cost : null} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">이익률</dt>
                    <dd className="tabular-nums">{marginCell(row)}</dd>
                  </div>
                </dl>
                {amountField(row, draft, "default")}
                {memoField(row, draft, "default")}
              </ListCard>
            ))}
            <ListCard>
              <div className="flex items-center justify-between gap-x-2">
                <b>합계</b>
                <Money fallback={EMPTY_MARK} signed value={liveDiff} />
              </div>
              <p className="text-xs text-muted-foreground">
                판매 <Money value={totals.sale} /> · 원가 <Money value={profit.no_cost ? null : totals.cost} /> · 이익률 {rateText(totals.profit_rate_bp)}
              </p>
              <p className="text-xs text-muted-foreground">
                + 관리비 <Money value={totals.mgmt} /> · 절사 (부가세 빼고)
                {totals.supply_profit_rate_bp !== null && <> · 관리비 넣으면 {rateText(totals.supply_profit_rate_bp)}</>}
              </p>
              {missingNote && <p className="text-xs text-warning">{missingNote}</p>}
            </ListCard>
          </ul>
        )}
      </SectionCard>

      {lockedReason === null && (
        <SaveBar
          blocked={blocked}
          dirty={changed.length > 0}
          label={changed.length > 0 ? `저장 (${changed.length})` : "저장"}
          pending={pending}
          onSave={onSave}
        />
      )}
    </div>
  );
};

export default QuoteProfit;
